"use client"

import React from "react"
import dynamic from "next/dynamic"
import { cn } from "@/lib/utils"
import type { ReactPlayerProps } from "react-player"

type VideoPlayerProps = {
  url: string
  className?: string
  thumbnail?: React.ReactNode
  loadingComponent?: React.ReactNode
}

const DEFAULT_LOADER = (
  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg animate-pulse" />
)

const PlayIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={cn("w-6 h-6", className)}
  >
    <path d="M8 5v14l11-7z" />
  </svg>
)

const DEFAULT_THUMBNAIL = (
  <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-md">
      <PlayIcon className="w-8 h-8 text-gray-700 ml-1" />
    </div>
  </div>
)

const LazyReactPlayer = dynamic(() => import("react-player/lazy"), {
  ssr: false,
  loading: () => DEFAULT_LOADER,
}) as React.FC<ReactPlayerProps>

const VideoPlayer = React.forwardRef<HTMLDivElement, VideoPlayerProps>(
  ({ url, className = "", thumbnail = DEFAULT_THUMBNAIL }, ref) => {
    const [isReady, setIsReady] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)

    const handleReady = () => setIsReady(true)
    const handleError = () => setHasError(true)

    return (
      <div
        ref={ref}
        className={cn(
          "relative aspect-video bg-black overflow-hidden",
          "group transition-all duration-300",
          className,
        )}
      >
        {!isReady && !hasError && thumbnail}

        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-300",
            hasError ? "opacity-0" : "opacity-100",
          )}
        >
          <LazyReactPlayer
            url={url}
            width="100%"
            height="100%"
            controls
            playing={false} // use `playing` prop to control playback
            onReady={handleReady}
            onError={handleError}
            config={{
              vimeo: {
                playerOptions: {
                  color: "00adef",
                  byline: false,
                  portrait: false,
                  title: false,
                  controls: 1, // Show controls but customize them
                  download: false, // Explicitly disable download button
                },
              },
              youtube: {
                playerVars: {
                  modestbranding: 1,
                  rel: 0,
                  disablekb: 1, // Disables keyboard controls
                  controls: 1, // Show standard controls
                  fs: 0, // Disable fullscreen button (optional)
                },
              },
              file: {
                attributes: {
                  controlsList: "nodownload", // Disables download for HTML5 video
                },
              },
            }}
          />
        </div>

        {hasError && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center gap-4 p-6">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-md">
              <PlayIcon className="w-8 h-8 text-gray-700 ml-1" />
            </div>
            <p className="text-gray-600 font-medium">
              We couldn&apos;t load this video content
            </p>
            <button
              onClick={() => setHasError(false)}
              className="px-4 py-2 bg-white text-gray-800 rounded-md text-sm font-medium shadow-sm hover:bg-gray-50 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    )
  },
)

VideoPlayer.displayName = "VideoPlayer"

export default VideoPlayer
